import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { FOOTER_HEIGHT } from '@/theme';
import { mq } from '@/theme/scalingTokens';

const NARROW_VIEWPORT = '@media (max-width: 639px)';
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

const ENTER_DURATION = '420ms';
const EXIT_DURATION = '220ms';
const ENTER_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)'; // ease-out, perceptible glide
const EXIT_EASING = 'cubic-bezier(0.4, 0, 1, 1)'; // ease-in for a crisp dismissal

export const FLOATING_ACTION_BAR_EXIT_MS = 220;

/**
 * CSS custom properties used by consumers to anchor the bar to a specific
 * horizontal region (instead of spanning full viewport width) and to override
 * the inner content max-width. Setting these inline on the component element
 * is the supported path; no CSS property values appear in component JSX.
 */
export const FLOATING_ACTION_BAR_ROOT_LEFT_VAR = '--fab-root-left';
export const FLOATING_ACTION_BAR_ROOT_RIGHT_VAR = '--fab-root-right';
export const FLOATING_ACTION_BAR_INNER_MAX_WIDTH_VAR = '--fab-inner-max-width';

export const useFloatingActionBarStyles = makeStyles({
  root: {
    position: 'fixed',
    left: `var(${FLOATING_ACTION_BAR_ROOT_LEFT_VAR}, 0)`,
    right: `var(${FLOATING_ACTION_BAR_ROOT_RIGHT_VAR}, 0)`,
    bottom: FOOTER_HEIGHT,
    zIndex: 90,
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    willChange: 'transform, opacity',
    [mq.highZoom]: {
      position: 'static' as const,
      zIndex: 'auto',
      bottom: 'auto',
    },
  },
  enter: {
    animationName: {
      from: { transform: 'translateY(1.5rem)', opacity: 0 },
      to: { transform: 'translateY(0)', opacity: 1 },
    },
    animationDuration: ENTER_DURATION,
    animationTimingFunction: ENTER_EASING,
    animationFillMode: 'both',
    [REDUCED_MOTION]: {
      animationName: 'none',
      animationDuration: '0ms',
    },
  },
  exit: {
    animationName: {
      from: { transform: 'translateY(0)', opacity: 1 },
      to: { transform: 'translateY(1.5rem)', opacity: 0 },
    },
    animationDuration: EXIT_DURATION,
    animationTimingFunction: EXIT_EASING,
    animationFillMode: 'forwards',
    [REDUCED_MOTION]: {
      animationName: 'none',
      animationDuration: '0ms',
    },
  },
  /**
   * Default chrome: full-width opaque surface with border-top and shadow.
   * Used when the floating children are bare controls (e.g., Back/Save/Next
   * button row) that need a backdrop to be readable over scrolling content.
   */
  rootWithChrome: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopWidth: '0.0625rem',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    boxShadow: tokens.shadow8,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXXL),
    [NARROW_VIEWPORT]: {
      ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    },
  },
  /**
   * Bare mode: no wrapper background, border, or padding. Children render
   * exactly as they would inline (e.g., a self-contained card such as
   * `BookAnotherCard` that already has its own surface treatment).
   */
  rootBare: {
    ...shorthands.padding(0, tokens.spacingHorizontalXXL),
    [NARROW_VIEWPORT]: {
      ...shorthands.padding(0, tokens.spacingHorizontalM),
    },
  },
  /**
   * Variant applied when the consumer has anchored the bar via rootLeft /
   * rootRight CSS vars: the bar already spans the exact target region, so the
   * default horizontal padding (which keeps bare children off the viewport
   * edges) must be zeroed out.
   */
  rootAnchored: {
    ...shorthands.padding(0, 0),
    [NARROW_VIEWPORT]: {
      ...shorthands.padding(0, 0),
    },
  },
  inner: {
    width: '100%',
    maxWidth: `var(${FLOATING_ACTION_BAR_INNER_MAX_WIDTH_VAR}, 75rem)`,
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalM),
  },
});
